
import AppConfig from '@/AppConfig';
import { Team } from '@/classes/auth';
import { Plan, Subscription, SubscriptionState } from '@/classes/stripe';
import Alert from '@/components/Alert.vue';
import HelpTooltip from '@/components/HelpTooltip.vue';
import Icon from '@/components/Icons/Icon.vue';
import AddressWithCombobox from '@/components/PaymentForm/AddressWithCombobox.vue';
import EmailAndNotes from '@/components/PaymentForm/EmailAndNotes.vue';
import HeaderComponent from '@/components/PaymentForm/Header.vue';
import PaymentFormSidebar from '@/components/PaymentForm/PaymentFormSidebar.vue';
import PaymentMethodComponent from '@/components/PaymentForm/PaymentMethod.vue';
import StripePaymentInformation from '@/components/PaymentForm/StripePaymentInformation.vue';
import VeeValidateRefs from '@/Models/VeeValidateRefs';
import EventService from '@/Services/EventService';
import { trackCompletePurchaseButton } from '@/Services/Snowplow/SnowplowService';
import StripeCheckoutService from '@/Services/Subscription/StripeCheckoutService';
import StripePaymentMethodService from '@/Services/Subscription/StripePaymentMethodService';
import StripeSubscriptionService from '@/Services/Subscription/StripeSubscriptionService';
import store from '@/store';
import { AuthState } from '@/store/auth';
import { LocationState } from '@/store/location';
import * as Sentry from '@sentry/vue';
import axios, { AxiosError } from 'axios';
import { debounce } from 'lodash';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { mapState } from 'vuex';

@Component({
  components: {
    Icon,
    Alert,
    HelpTooltip,
    AddressWithCombobox,
    StripePaymentInformation,
    PaymentFormSidebar,
    HeaderComponent,
    PaymentMethodComponent,
    EmailAndNotes,
    ValidationObserver,
    ValidationProvider,
  },
  computed: {
    ...mapState<SubscriptionState>('subscription', {
      activeSubscription: (state: SubscriptionState) => state.activeSubscription,
      activePlan: (state: SubscriptionState) => state.activePlan,
      subscriptionPlan: (state: SubscriptionState) => state.selectedPlan,
    }),
    ...mapState<LocationState>('location', {
      isVatRateLoading: (state: LocationState) => state.showLoading.loadingVatRate,
    }),
    ...mapState<AuthState>('auth', {
      team: (state: AuthState) => new Team(state.team),
    }),
  },
})
export default class PaymentForm extends Vue {
  @Prop({ default: '', type: String })
  planId!: string;

  @Prop({ default: '' })
  countryIso2!: string;

  isCNV1609AbTest: boolean = false;

  team!: Team;
  activeTab?: any = 0;
  // state from vuex
  activeSubscription!: Subscription;
  // state from vuex
  activePlan!: Plan;
  // state from vuex
  isVatRateLoading!: boolean;
  // state from vuex
  subscriptionPlan!: Plan;
  // Handles business logic and form state
  stripeCheckoutService: StripeCheckoutService = new StripeCheckoutService();
  // Handles payment method logic and form state
  stripePaymentMethodService: StripePaymentMethodService = new StripePaymentMethodService();
  // Handles payment method logic and form state
  stripeSubscriptionService: StripeSubscriptionService = new StripeSubscriptionService();
  // selected subscription plan
  selectedPlan: Plan = new Plan();
  // This is the stepper indicator, actually it is unused, because we fake the steps
  stepper = 2;
  // Prevents to many vat rate calculations
  throttleUpdateVatRate = debounce(async () => await this.stripeCheckoutService.updateCustomerVatRate(), 1000, {
    leading: false,
    trailing: true,
  });
  // Prevents double click issues, only last click counts
  throttleSubmit = debounce(this.submit, 300, { leading: false, trailing: true });
  // Prevents double click issues and shows state of saving
  isSaving = false;
  checkoutType = 'new';
  isUpdating = true;

  formErrorMessage = '';
  formStripeErrorMessage = false;
  formErrorMessageCreditCardNumber: boolean = false;
  formErrorMessageCreditCardDate: boolean = false;
  formErrorMessageCreditCardCvcNumber: boolean = false;
  formErrorMessageCreditCardPostalCode: boolean = false;

  errorContainer = '';

  $refs!: {
    // Used for validation of the form data e.g. customer, address etc.
    CreditCardCvcNumber: any;
    CreditCardNumber: any;
    CreditCardDate: any;
    CreditCardPostalCode: any;
    firstName: any;

    observer: VeeValidateRefs;

    // Container for payment-information
    payment_information: any;

    // Used for stripe elements form as a mounting point
    cardNumber: any;
    cardExpiry: any;
    cardCvc: any;
    cardZip: any;

    // Error alert
    errorAlert: any;
    checkoutStepContainer: any;
    stripePaymentInformation: any;
  };

  // Just legal entities ready for selection
  legalEntityB2C: string = 'b2c';

  /**
   * If type changes due to clicking on payment selection trigger a change
   * of the payment method form.
   *
   * @param newVal
   */
  @Watch('stripePaymentMethodService.paymentType', { deep: true, immediate: false })
  onPaymentMethodUpdate(newVal: string): void {
    this.formErrorMessageCreditCardNumber = false;
    this.formErrorMessageCreditCardDate = false;
    this.formErrorMessageCreditCardCvcNumber = false;
    this.formErrorMessageCreditCardPostalCode = false;
    this.formErrorMessage = '';
    this.formStripeErrorMessage = false;
    this.rebuildStripeForm(newVal);
  }

  @Watch('selectedPlan', { deep: true, immediate: false })
  onSelectedPlanUpdate(selectedPlan: Plan): void {
    this.subscriptionCheckoutType(selectedPlan.id);
  }

  @Watch('activePlan.id', { deep: true, immediate: false })
  onActivePlanUpdate(): void {
    if (this.subscriptionPlan.id === this.activePlan.id && !this.subScriptionHasIncompletePayment()) {
      this.$router.push({ name: 'checkout_plan_selection' });
    }
  }

  // Setup the components needed data, like customer data.
  async created(): Promise<void> {
    await this.handlePlanState();

    // Handle country overwrite via URL prop
    if (this.countryIso2.length > 0) {
      await this.stripeCheckoutService.selectCountry(this.countryIso2);
    }

    this.handlePrefillData();
    // set active tab to b2c, if user is b2c
    if (this.team.legal_entity === 'b2c') {
      this.activeTab = 1;
      this.stripeCheckoutService.legalEntity = this.legalEntityB2C;
    }

    this.subscriptionCheckoutType(this.selectedPlan.id);

    await this.getAllowedPaymentMethodsForUser();
    this.isUpdating = false;

    EventService.createEvent(EventService.VISITED_CHECKOUT, {
      paramPlanId: this.planId || null,
      paramCountryIso: this.countryIso2 || null,
    });
  }

  async mounted(): Promise<void> {
    // Mount the payment form, we do this here because refs is not available in created()!
    this.rebuildStripeForm(this.stripePaymentMethodService.paymentType);
    await this.$nextTick();

    this.isCNV1609AbTest = Boolean(this.team.ab_tests.length && this.team.ab_tests.includes('CNV_1609'));
  }

  subScriptionHasIncompletePayment(): boolean {
    return ['incomplete', 'past_due'].includes(this.activeSubscription.status);
  }

  async getAllowedPaymentMethodsForUser(): Promise<void> {
    await this.stripePaymentMethodService.getAllowedPaymentMethodsForUser(
      this.stripeCheckoutService.country.country_iso2
    );
  }

  // Mainly handling of plan state, is a plan selected or not and how to react if not
  async handlePlanState(): Promise<void> {
    // Without a plan (or the same as active) a checkout is not possible so we redirect the customer to the selection page
    if (
      (!this.subscriptionPlan?.id || this.subscriptionPlan?.id === this.activePlan.id) &&
      !this.subScriptionHasIncompletePayment()
    ) {
      await this.$router.push({ name: 'checkout_plan_selection' });
    }
  }

  get currentPlanTranslationKey(): string | undefined {
    return this.stripeSubscriptionService.findPlan(this.activeSubscription.plan_id)?.translationKey;
  }

  get currentPlanEndDate(): string {
    return this.activeSubscription.current_period_end.format('MMMM D, YYYY');
  }

  get isCardOnlyPaymentMethodCNV1609AbTest(): boolean {
    return (
      this.stripePaymentMethodService.paymentMethodsToShow.length === 0 &&
      this.stripePaymentMethodService.paymentMethodsToShow[0] === 'card' &&
      this.isCNV1609AbTest
    );
  }

  // We fill the form for the customer to ease the pain of filling out
  handlePrefillData(): void {
    // Load plan from vuex
    Object.assign(this.selectedPlan, new Plan(this.subscriptionPlan));

    // Init customer data for prefilling the form
    this.stripeCheckoutService.initCustomerData();

    // Setup subscription for the service
    this.stripeSubscriptionService.initSubscription();
  }

  // Rebuild stripe elements form an exception is send_invoice which doesn't need stripe elements
  rebuildStripeForm(paymentMethodType: string): void {
    switch (paymentMethodType) {
      case 'card':
        this.stripePaymentMethodService.initCardForm();
        break;
      case 'sepa_debit':
        this.stripePaymentMethodService.initSEPAForm();
        break;
    }
  }

  async forceLogout(): Promise<void> {
    await this.$store.dispatch('auth/logout');
    const authUrl: string = AppConfig.getAuthServiceUrl() || '';
    window.location.href = `${authUrl}/logout`;
  }

  /**
   * How this works:
   * - Create a subscription with incomplete status
   * - Use intent from the latest_invoice of this subscription and hook into stripe element payment form
   * - Trigger a card payment and save the payment method in stripe
   * - Save payment method as well in our API // TODO can later on be outsourced to webhooks maybe
   * - Redirect on success, stay on failure
   */
  async submit(): Promise<void> {
    const language = store.state?.auth?.user?.language;
    const user_id = store.state?.auth?.user?.id;
    const account = store.state?.account?.account;
    const price_currency_displayed = store.state?.auth?.team?.currency;
    trackCompletePurchaseButton({
      industry_id: account?.industry_id ?? null,
      user_id,
      price_currency_displayed,
      language_code: language,
    });

    this.formStripeErrorMessage = false;
    this.formErrorMessage = '';

    if ((await this.$refs.observer.validate()) && this.selectedPlan.id && this.isSubmitPossible) {
      if (this.stripePaymentMethodService.paymentType === 'card') {
        this.$refs.stripePaymentInformation.hasClass();
      }

      if (this.stripeCheckoutService.country.vat_rate === null) {
        this.formErrorMessage = this.$t('fields.checkout.form.invalid_tax').toString();
        this.scrollToErrorNew('checkoutPaymentInformationContainer');
        return;
      }

      this.isSaving = true;
      this.$store.commit('layout/toggleGlobalLoader', true);
      this.$store.commit('layout/toggleGlobalOverlay', true);

      /**
       * We need a default payment method for a subscription.
       * A subscription create must therefore create a new payment method, attach oit to the customer as default
       */
      const paymentMethodStoreResult = await this.submitPaymentMethod();
      const response = paymentMethodStoreResult?.response;
      const type = this.checkoutType;

      switch (paymentMethodStoreResult?.status ?? paymentMethodStoreResult?.response?.status) {
        case 422:
          if (response.data.errors.hasOwnProperty('customer.tax_id_data.value')) {
            this.formErrorMessage = this.$t('fields.checkout.form.invalid_vat_id').toString();
          } else {
            this.formErrorMessage = this.$t('fields.checkout.form.invalid').toString();
          }

          this.errorContainer = 'checkoutBillingAddressContainer';

          break;
        case 402:
          this.formStripeErrorMessage = true;
          // Check if card is reported lost or stolen and force logout the user
          if (['lost_card', 'stolen_card'].includes(response.data.decline_code)) {
            await this.forceLogout();
          }
          const errorMessage = this.$t(
            'fields.checkout.form.stripe_errors.' + (response.data.decline_code || response.data.error_code)
          ).toString();
          this.formErrorMessage = this.$t(errorMessage).toString();

          break;
        case 200:
          await this.submitSubscription(this.selectedPlan.id)
            .then(async (subscriptionResult: any) => {
              // TODO use switch here for the different payment types later on
              // Everything is good, redirect to success page!
              if (subscriptionResult && this.stripePaymentMethodService.paymentType === 'card') {
                const confirmPaymentResult = await this.stripePaymentMethodService.stripe.confirmCardPayment(
                  subscriptionResult
                );

                if (!confirmPaymentResult.error) {
                  await this.$router.push({ name: 'checkout_payment_complete', params: { type } });
                } else {
                  if (confirmPaymentResult.error.decline_code) {
                    this.formErrorMessage = this.$t(
                      'fields.checkout.form.stripe_errors.' + confirmPaymentResult.error.decline_code
                    ).toString();
                  } else {
                    this.formErrorMessage = this.$t('fields.checkout.form.invalid').toString();
                  }
                  this.errorContainer = 'checkoutPaymentInformationContainer';
                }
              } else {
                await this.$router.push({ name: 'checkout_payment_complete', params: { type } });
              }
            })
            .catch(() => {
              this.formErrorMessage = this.$t('fields.checkout.form.invalid').toString();
              this.errorContainer = 'checkoutPaymentInformationContainer';
            });
          break;

        default:
          if (this.stripePaymentMethodService.stripeErrorMessage) {
            this.formErrorMessage = this.$t(
              'fields.checkout.form.stripe_errors.' + this.stripePaymentMethodService.stripeError
            ).toString();
            this.errorContainer = 'checkoutPaymentInformationContainer';
          }
      }

      this.isSaving = false;
    } else {
      this.formErrorMessage = this.$t('fields.checkout.form.invalid').toString();
      this.errorContainer = 'checkoutBillingAddressContainer';
    }

    if (this.formErrorMessage !== '') {
      this.scrollToError(this.$refs.errorAlert);
    }

    this.$store.commit('layout/toggleGlobalLoader', false);
    this.$store.commit('layout/toggleGlobalOverlay', false);
  }

  scrollToErrorNew(id: string, topNegativeOffset: number = -150): void {
    // document?.getElementById('errorAlert')?.classList.add('alert--fixed');
    const yOffset = topNegativeOffset;
    const element = document.getElementById(id);
    if (element) {
      const y = (element as HTMLElement).getBoundingClientRect()?.top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }

  // Scroll to the nearest error from submit upwards
  scrollToError(element: any = null): void {
    if (element) {
      this.$vuetify.goTo(element, { offset: 240 });
    } else {
      const entry: any = Object.keys(this.$refs.observer.errors)
        .reverse()
        .find((entry: any) => {
          return this.$refs.observer.errors[entry].length > 0;
        });

      if (typeof entry !== 'undefined' && entry in this.$refs) {
        //@ts-ignore
        this.$vuetify.goTo(this.$refs[entry], { offset: 240 });
      }
    }
  }

  // Save the payment method as new default
  async submitPaymentMethod(): Promise<any> {
    this.stripeCheckoutService.customer.address.country = this.stripeCheckoutService.country.country_iso2;
    return this.stripePaymentMethodService.savePaymentMethod({
      legal_entity: this.stripeCheckoutService.legalEntity,
      customer: this.stripeCheckoutService.customer,
    });
  }

  // Subscribe to the new plan, either an update or a complete new one
  async submitSubscription(planId: string): Promise<any> {
    const ss = this.stripeSubscriptionService;

    if (!ss.isPlanUpcoming(planId)) {
      return await axios({
        method: ss.subscription.id ? 'patch' : 'post',
        url: AppConfig.getAPIBaseUrl() + '/subscription/stripe/' + (ss.subscription.id ? planId : ''),
        data: {
          plan_id: ss.subscription.id ? planId : this.selectedPlan.id,
          payment_method_type: this.stripePaymentMethodService.paymentType,
        },
      })
        .then(async (response) => {
          if (response?.data?.client_secret) {
            return response.data.client_secret;
          }
          await store.dispatch('subscription/setActiveSubscription', response.data);
        })
        .catch((error: AxiosError) => {
          Sentry.captureException(error);
        });
    }
  }

  // No saving process going on and stripe form is ready?
  get isSubmitPossible(): boolean {
    return !this.isSaving;
  }

  // Sets the checkout-type (downgrade, upgrade, new)
  subscriptionCheckoutType(planId: string): void {
    const isUpgrade = this.stripeSubscriptionService.isPlanSuperior(planId);

    if (isUpgrade !== null) {
      this.checkoutType = isUpgrade ? 'upgrade' : 'downgrade';
    }
  }

  @Watch('stripeCheckoutService.legalEntity', { deep: true, immediate: false })
  async onLegalEntityChange(newVal: string): Promise<void> {
    if (newVal) {
      this.isUpdating = true;
      await this.throttleUpdateVatRate();
      setTimeout(() => {
        this.isUpdating = false; // Set to false after 1 second to prevent fast flashing of loader
      }, 1000);
    }
  }

  @Watch('stripeCheckoutService.customer.address', { deep: true, immediate: false })
  async onCityChange(newVal: string): Promise<void> {
    if (newVal) {
      this.isUpdating = true;
      await this.throttleUpdateVatRate();
      setTimeout(() => {
        this.isUpdating = false; // Set to false after 1 second to prevent fast flashing of loader
      }, 1000);
    }
  }
}
